// Intent: the compact month calendar inside the pull-down summary (mock
// poprawione_podsumowanie_linia). Tapping a day shows the expense-line chart
// as of that day. Dots mark days that HAVE expenses (real, day-stamped data —
// works retroactively); future days are dimmed and dead (nothing was ever
// forecast for them). Grid math shared with the date picker via utils/calendar.

import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { AppText } from '@/components/ui/app-text';
import { colors, radius, spacing } from '@/theme/tokens';
import { isoOf, monthCells } from '@/utils/calendar';

interface SummaryCalendarProps {
  viewYear: number;
  viewMonth: number; // 0-based
  selectedIso: string | null;
  todayIso: string;
  /** Day has budget entries — draws the dot. */
  hasData: (iso: string) => boolean;
  onSelect: (iso: string) => void;
  onShiftMonth: (delta: -1 | 1) => void;
}

export function SummaryCalendar({
  viewYear,
  viewMonth,
  selectedIso,
  todayIso,
  hasData,
  onSelect,
  onShiftMonth,
}: SummaryCalendarProps) {
  const { t } = useTranslation();
  const cells = monthCells(viewYear, viewMonth);
  // Months are i18n-indexed keys (forecastHistory.month.1..12) — never a JSON
  // array (react-i18next types returnObjects as string).
  const monthName = t(`forecastHistory.month.${viewMonth + 1}`);
  const canGoNext = isoOf(viewYear, viewMonth, 1) < todayIso.slice(0, 8) + '01';

  return (
    <View style={{ gap: spacing.xs }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Pressable accessibilityRole="button" accessibilityLabel={t('forecastHistory.prevMonth')} hitSlop={8} onPress={() => onShiftMonth(-1)}>
          <Ionicons name="chevron-back" size={20} color={colors.primary} />
        </Pressable>
        <AppText variant="headlineSm" style={{ flex: 1, textAlign: 'center' }}>
          {monthName} {viewYear}
        </AppText>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('forecastHistory.nextMonth')}
          hitSlop={8}
          disabled={!canGoNext}
          onPress={() => onShiftMonth(1)}
          style={{ opacity: canGoNext ? 1 : 0.3 }}
        >
          <Ionicons name="chevron-forward" size={20} color={colors.primary} />
        </Pressable>
      </View>

      <View style={{ flexDirection: 'row' }}>
        {[1, 2, 3, 4, 5, 6, 7].map((d) => (
          <AppText key={d} variant="labelMd" color={colors.outline} style={{ flex: 1, textAlign: 'center' }}>
            {t(`forecastHistory.weekday.${d}`)}
          </AppText>
        ))}
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {cells.map((day, i) => {
          if (day === null) return <View key={`e-${i}`} style={{ width: `${100 / 7}%`, height: 40 }} />;
          const iso = isoOf(viewYear, viewMonth, day);
          const isFuture = iso > todayIso;
          const isToday = iso === todayIso;
          const isSelected = iso === selectedIso;
          const dot = !isFuture && hasData(iso);
          return (
            <Pressable
              key={iso}
              accessibilityRole="button"
              disabled={isFuture}
              onPress={() => onSelect(iso)}
              style={{ width: `${100 / 7}%`, height: 40, alignItems: 'center', justifyContent: 'center' }}
            >
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: radius.full,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSelected ? colors.navy : 'transparent',
                  borderWidth: isToday && !isSelected ? 2 : 0,
                  borderColor: colors.primary,
                }}
              >
                <AppText
                  variant="labelLg"
                  color={isSelected ? '#FFFFFF' : isFuture ? colors.outlineVariant : colors.textPrimary}
                >
                  {day}
                </AppText>
              </View>
              <View
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  marginTop: 1,
                  backgroundColor: dot && !isSelected ? colors.primary : 'transparent',
                }}
              />
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}
